import { Link } from "react-router-dom";
import { ArrowRight, Heart, Handshake, MapPin } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { IMAGES, PHONE, PHONE_TEL, EMAIL } from "../data";

const VALUES = [
  { icon: Heart, title: "Built on pride", text: "Every install is a home someone loves. We treat your property like it's the one we live in." },
  { icon: Handshake, title: "Straightforward pricing", text: "Clear quotes, no surprise fees — and we'll beat any competitor's price, every time." },
  { icon: MapPin, title: "Local & loyal", text: "Born and based in Salt Lake County. Utah, Davis & Weber counties served for a small travel fee." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-20">
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-16 pb-20 sm:pb-28">
        <Reveal>
          <Overline>About us</Overline>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-slate-100 tracking-tight leading-[1.1] max-w-3xl">
            Small start. <span className="text-gold-gradient">Big glow.</span>
          </h1>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-white/5 h-80 sm:h-[480px]">
              <img src={IMAGES.installer} alt="GlowMode installer at work" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
              <p className="absolute bottom-5 left-5 text-xs font-mono uppercase tracking-[0.25em] text-gold-light">On the job, Salt Lake County</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-6 text-slate-300 leading-relaxed text-base sm:text-lg">
              <p>
                GlowMode Lighting started the way most good things do — small. One ladder, one truck, and a belief that a home at night should look every bit as beautiful as it does in the daylight.
              </p>
              <p>
                What began as helping neighbors hang their Christmas lights turned into something bigger. People didn't just want lights up — they wanted it done <em className="text-gold-light not-italic font-semibold">right</em>. Custom cut to their roofline. No wires hanging. No burnt-out bulbs by mid-December. And no spending their weekends on an icy ladder.
              </p>
              <p>
                Today we light homes and businesses across Salt Lake County — permanent trim, holiday displays, landscapes, weddings, even automobiles — but the promise hasn't changed: show up on time, do flawless work, and beat every competitor's price.
              </p>
              <p className="font-serif text-xl sm:text-2xl text-slate-100 italic border-l-2 border-gold pl-6">
                "Life looks better in Glow Mode. We just make sure your place proves it."
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/5 bg-charcoal/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24">
          <div className="grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div data-testid={`about-value-${i}`} className="h-full rounded-2xl border border-white/5 bg-card2/60 p-8 transition-all duration-300 hover:border-gold/50 hover:-translate-y-1">
                  <v.icon className="h-8 w-8 text-gold mb-5" />
                  <h3 className="font-serif text-xl font-medium text-slate-100 mb-3">{v.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
        <div className="grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <Overline>Where we work</Overline>
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-100 tracking-tight mb-6">
              Proudly serving Salt Lake County
            </h2>
            <p className="text-slate-400 leading-relaxed mb-6">
              Every city in Salt Lake County is home turf — Salt Lake City, Sandy, Draper, South Jordan, West Jordan, Holladay, Murray, Millcreek, Cottonwood Heights, Herriman, Riverton, Taylorsville, West Valley, Magna, and beyond.
            </p>
            <p className="text-slate-400 leading-relaxed mb-8">
              Outside the county? We regularly serve Utah, Davis, and Weber counties for a small travel fee — quoted upfront, never a surprise.
            </p>
            <Link
              to="/book"
              data-testid="about-book-button"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-obsidian bg-gradient-to-br from-gold-light via-gold to-gold-dark transition-all duration-300 hover:shadow-[0_0_35px_rgba(212,175,55,0.5)] hover:-translate-y-0.5"
            >
              Book Your Free Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-gold/20 bg-card2/50 p-8 sm:p-12">
              <h3 className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-8">Reach us directly</h3>
              <div className="space-y-6">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Call or text</p>
                  <a href={PHONE_TEL} data-testid="about-phone-link" className="font-serif text-2xl sm:text-3xl text-slate-100 hover:text-gold-light transition-colors">{PHONE}</a>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Email</p>
                  <a href={`mailto:${EMAIL}`} data-testid="about-email-link" className="text-base sm:text-lg text-slate-200 hover:text-gold-light transition-colors break-all">{EMAIL}</a>
                </div>
                <div className="pt-6 border-t border-white/5">
                  <p className="text-sm text-slate-500">GlowModeLighting.com · Salt Lake County, UT</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
